import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import MainNavbar from "./components/navbar";
import ScrollToTop from "./components/ScrollToTop";
import SiteModeProvider from "./components/SiteModeProvider";
import CookieConsentProvider from "./components/CookieConsent/CookieConsentContext";
import { RETAIL_ROUTES, LEGACY_REDIRECTS } from "./lib/siteMode";
import Footer from "./components/footer";
import Container from "react-bootstrap/Container";
// Shipped in the main bundle, not code-split — these are the pages cold
// traffic (a fresh page load, e.g. an ad click) actually lands on, so
// lazy-loading them meant an extra async chunk fetch raced the initial
// render: on slow mobile connections the navbar + empty <main> + footer
// painted first, then the page content popped in seconds later.
import VallalatiHome from "./landings/VallalatiHome";
import LakossagiHome from "./landings/LakossagiHome";
import SzemelyiKolcson from "./landings/retail/SzemelyiKolcson";

// Every other landing page is only ever needed once a visitor actually
// navigates to it, so they're code-split into separate chunks instead of
// all shipping in the main bundle on every page load.
const DualEducation = lazy(() => import("./landings/corporate/DualEducation"));
const NemzetiBajnokok = lazy(() =>
  import("./landings/corporate/NemzetiBajnokok")
);
const ElmaradottTersegBruhazas = lazy(() =>
  import("./landings/corporate/ElmaradottTersegBruhazas")
);
const BusinessLoanConsulting = lazy(() =>
  import("./landings/corporate/BusinessLoanConsulting")
);
const CorporateAccountOptimize = lazy(() =>
  import("./landings/corporate/CorporateAccountOptimize")
);
const PrivacyPolicy = lazy(() => import("./landings/PrivacyPolicy"));
const Impresszum = lazy(() => import("./landings/Impresszum"));
const Rolunk = lazy(() => import("./landings/Rolunk"));
const ThanksPage = lazy(() => import("./landings/ThanksPage"));
const ThanksPageRetail = lazy(() =>
  import("./landings/retail/ThanksPageRetail")
);
const FrameworkContract = lazy(() =>
  import("./landings/corporate/FrameworkContract")
);
const Jelzaloghitel = lazy(() => import("./landings/retail/Jelzaloghitel"));
const LakossagiBankszamla = lazy(() =>
  import("./landings/retail/LakossagiBankszamla")
);
const Nyugdijmegtakaritas = lazy(() =>
  import("./landings/retail/Nyugdijmegtakaritas")
);
const Eletbiztositas = lazy(() => import("./landings/retail/Eletbiztositas"));
const ArticlePage = lazy(() => import("./landings/ArticlePage"));

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <SiteModeProvider>
        <CookieConsentProvider>
          <MainNavbar />
          <main>
            <Suspense
              fallback={
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "60vh" }}
                >
                  <div
                    className="spinner-border"
                    role="status"
                    style={{ color: "var(--warning-color)" }}
                  >
                    <span className="visually-hidden">Betöltés...</span>
                  </div>
                </div>
              }
            >
              <Routes>
                {/* Retail (lakossági) — default page at "/" */}
                <Route path="/" element={<LakossagiHome />} />
                <Route path="/lakossagi" element={<LakossagiHome />} />

                {/* Corporate (vállalati) */}
                <Route path="/vallalati" element={<VallalatiHome />} />
                <Route
                  path="/nemzeti-bajnokok"
                  element={<NemzetiBajnokok />}
                />
                <Route path="/dualis-kepzes" element={<DualEducation />} />
                <Route
                  path="/elmaradott-tersegek-tamogatasa"
                  element={<ElmaradottTersegBruhazas />}
                />
                <Route
                  path="/vallalati-hitel"
                  element={<BusinessLoanConsulting />}
                />
                <Route
                  path="/szamlaoptimalizalas"
                  element={<CorporateAccountOptimize />}
                />
                <Route
                  path="/penzugyi-orangyal"
                  element={<FrameworkContract />}
                />

                {/* Retail (lakossági) — nested products */}
                <Route
                  path={RETAIL_ROUTES.jelzaloghitel}
                  element={<Jelzaloghitel />}
                />
                <Route
                  path={RETAIL_ROUTES.szemelyiKolcson}
                  element={<SzemelyiKolcson />}
                />
                <Route
                  path={RETAIL_ROUTES.bankszamla}
                  element={<LakossagiBankszamla />}
                />
                <Route
                  path={RETAIL_ROUTES.nyugdijmegtakaritas}
                  element={<Nyugdijmegtakaritas />}
                />
                <Route
                  path={RETAIL_ROUTES.eletbiztositas}
                  element={<Eletbiztositas />}
                />

                {/* Legacy flat retail paths → nested equivalents */}
                {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                  <Route
                    key={from}
                    path={from}
                    element={<Navigate to={to} replace />}
                  />
                ))}

                {/* Shared */}
                <Route
                  path="/adatkezelesi-tajekoztato"
                  element={<PrivacyPolicy />}
                />
                <Route path="/impresszum" element={<Impresszum />} />
                <Route path="/rolunk" element={<Rolunk />} />
                <Route path="/koszonjuk" element={<ThanksPage />} />
                <Route
                  path="/koszonjuk-lakossagi"
                  element={<ThanksPageRetail />}
                />

                {/* SEO content articles — nested under /lakossagi so they
                    automatically pick up the retail theme via the existing
                    prefix matching in src/lib/siteMode.js, with no
                    special-casing needed there. Must stay last among the
                    /lakossagi/* routes: React Router v6+ ranks static path
                    segments above dynamic ones regardless of declaration
                    order, so this dynamic catch-all cannot shadow any of the
                    specific /lakossagi/<product> routes declared above it. */}
                <Route
                  path="/lakossagi/:articleSlug"
                  element={<ArticlePage />}
                />
              </Routes>
            </Suspense>
          </main>
          <Container className="py-0">
            <Footer />
          </Container>
        </CookieConsentProvider>
      </SiteModeProvider>
    </BrowserRouter>
  );
}
